import React, { useMemo, useState } from 'react';
import FilterBar from './FilterBar';
import {
  formatCurrency,
  formatDate,
  matchesDateRange,
  normalizeSearch,
  triggerCsvDownload,
  triggerExcelLikeDownload,
  todayIso,
} from './utils';
import TablePagination, { usePagination } from '../TablePagination';

export default function CancelledARTab({
  collections,
  sales,
  selectedBranch,
  isLoading,
  onViewSale,
}) {
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const filteredRows = useMemo(() => {
    const searchKey = normalizeSearch(search);

    return collections
      .filter((row) => String(row.status || '') === 'Cancelled')
      .filter((row) => selectedBranch === 'all' || String(row.branch || '') === selectedBranch)
      .filter((row) => {
        if (!searchKey) {
          return true;
        }

        const text = [
          row.client_name,
          row.product,
          row.agent_name,
          row.payment_reference,
          row.transaction_no,
        ]
          .map((value) => normalizeSearch(value))
          .join(' ');

        return text.includes(searchKey);
      })
      .filter((row) => matchesDateRange(row.created_at || row.date, dateFrom, dateTo));
  }, [collections, selectedBranch, search, dateFrom, dateTo]);

  const pagination = usePagination(filteredRows);

  function exportCsv() {
    triggerCsvDownload(
      `cancelled-ar-${todayIso()}.csv`,
      ['Client', 'Transaction No.', 'Product', 'Schedule', 'Amount', 'Remarks', 'Cancelled Date'],
      filteredRows.map((row) => [
        row.client_name || '',
        row.transaction_no || '',
        row.product || '',
        row.schedule_no ?? '',
        row.premium_amount ?? 0,
        row.payment_reference || '',
        formatDate(row.created_at || row.date),
      ])
    );
  }

  function exportExcel() {
    triggerExcelLikeDownload(
      `cancelled-ar-${todayIso()}.xls`,
      ['Client', 'Transaction No.', 'Product', 'Schedule', 'Amount', 'Remarks', 'Cancelled Date'],
      filteredRows.map((row) => [
        row.client_name || '',
        row.transaction_no || '',
        row.product || '',
        row.schedule_no ?? '',
        row.premium_amount ?? 0,
        row.payment_reference || '',
        formatDate(row.created_at || row.date),
      ])
    );
  }

  return (
    <div className="collections-tab-content">
      <FilterBar
        search={search}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onSearchChange={setSearch}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onExportCsv={exportCsv}
        onExportExcel={exportExcel}
      />

      {isLoading ? (
        <div className="dashboard-loading">Loading cancelled AR records...</div>
      ) : filteredRows.length === 0 ? (
        <div className="dashboard-empty">No cancelled AR records found.</div>
      ) : (
        <div className="dashboard-table-wrap">
          <table className="dashboard-table collections-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Transaction No.</th>
                <th>Product</th>
                <th>Schedule</th>
                <th>Amount</th>
                <th>Remarks</th>
                <th>Cancelled Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {pagination.pagedItems.map((row) => {
                const sale = sales.find((item) => Number(item.id) === Number(row.sale_id)) || null;

                return (
                  <tr key={row.id}>
                    <td>{row.client_name || '-'}</td>
                    <td className="collections-mono-cell">{row.transaction_no || '-'}</td>
                    <td>{row.product || '-'}</td>
                    <td>{row.schedule_no ?? '-'}</td>
                    <td className="collections-number">{formatCurrency(row.premium_amount)}</td>
                    <td>{row.payment_reference || '-'}</td>
                    <td>{formatDate(row.created_at || row.date)}</td>
                    <td>
                      <button
                        type="button"
                        className="profile-secondary-btn profile-secondary-btn-xs"
                        onClick={() => onViewSale(sale)}
                        disabled={!sale}
                      >
                        View Sale
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading ? <TablePagination {...pagination} /> : null}
    </div>
  );
}
